import { useState } from "react";
import type { ReconciliationCandidate } from "../../domain/territory-identity";
import type { SaveError } from "./useTerritoryIdentity";
import { useI18n } from "../../i18n";
import { cn } from "../cn";

const RECONCILE_ERROR_CODES = new Set([
  "STALE_TERRITORY",
  "IDENTITY_VERSION_CONFLICT",
  "RECONCILIATION_NOT_REQUIRED",
  "SURVIVOR_NOT_CANDIDATE",
  "TERRITORY_NOT_PUBLISHABLE",
  "UNTRUSTED_ORIGIN",
  "CSRF_REQUIRED",
  "CSRF_INVALID",
  "CSRF_FETCH_FAILED",
  "UNAUTHENTICATED",
  "TRANSACTION_UNAVAILABLE",
  "SEED_NOT_OWNED",
]);

/**
 * WO#4C — explicit owner-controlled Territory Identity reconciliation.
 *
 * Presents the server-derived participating identities and lets the owner pick
 * exactly ONE survivor. No automatic preselection (an explicit choice + an
 * explicit confirm are both required). Losing identities are superseded; no
 * metadata is merged (V1 has only Display Name).
 */
export function TerritoryReconciliation({
  candidates,
  reconciling,
  reconcileError,
  onConfirm,
}: {
  candidates: ReconciliationCandidate[];
  reconciling: boolean;
  reconcileError: SaveError | null;
  onConfirm: (survivorIdentityId: string) => void;
}) {
  const { t } = useI18n();
  const [selected, setSelected] = useState<string | null>(null);

  const reconcileErrorText = reconcileError
    ? RECONCILE_ERROR_CODES.has(reconcileError.code)
      ? t(`dashboard.reconciliation.errors.${reconcileError.code}`)
      : reconcileError.message
    : null;

  return (
    <div data-testid="territory-reconciliation" className="mt-4">
      <p data-testid="territory-reconciliation-intro" className="m-0 text-[12px] text-state-warning">
        {t("dashboard.reconciliation.intro", { n: candidates.length })}
      </p>

      <ul className="mt-4 space-y-2 list-none p-0 m-0" data-testid="territory-reconciliation-candidates">
        {candidates.map((c) => (
          <li key={c.identity_id}>
            <label
              data-testid={`territory-reconciliation-option-${c.identity_id}`}
              className={cn(
                "flex items-center gap-3 h-12 px-3 rounded-md border cursor-pointer transition-colors",
                selected === c.identity_id
                  ? "border-border-accent bg-obsidian"
                  : "border-border-soft hover:border-border-accent",
              )}
            >
              <input
                type="radio"
                name="reconcile-survivor"
                value={c.identity_id}
                data-testid={`territory-reconciliation-radio-${c.identity_id}`}
                checked={selected === c.identity_id}
                onChange={() => setSelected(c.identity_id)}
                className="accent-neon-teal"
              />
              <span className="flex-1 min-w-0">
                <span className="block font-mono text-[13px] text-text-primary truncate">
                  {c.display_name}
                </span>
                <span className="block font-mono text-[9.5px] uppercase tracking-[0.16em] text-text-muted">
                  {t(
                    c.binding.unit_count === 1
                      ? "dashboard.reconciliation.metaOne"
                      : "dashboard.reconciliation.metaOther",
                    { n: c.binding.unit_count, rev: c.revision },
                  )}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>

      {reconcileError && (
        <p data-testid="territory-reconciliation-error" className="mt-3 text-[11px] text-state-danger">
          {reconcileErrorText}
        </p>
      )}

      <button
        type="button"
        data-testid="territory-reconciliation-confirm"
        disabled={selected === null || reconciling}
        onClick={() => selected && onConfirm(selected)}
        className={cn(
          "mt-4 inline-flex items-center gap-2 h-9 px-5",
          "font-mono text-[11px] font-medium tracking-[0.16em] uppercase",
          "text-text-inverse bg-text-primary rounded-md hover:opacity-90",
          "disabled:opacity-50 disabled:cursor-not-allowed",
        )}
      >
        {reconciling ? t("dashboard.reconciliation.reconciling") : t("dashboard.reconciliation.confirmSurvivor")}
      </button>
    </div>
  );
}
